import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Disclosure } from '@headlessui/react';
import {
  ChevronDownIcon,
  PlusIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  CalculatorIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { vendorComplianceApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import { useTeamId } from '../hooks/useTeamId';
import { PageHeader, StatusBadge, StatCard, EmptyState, LoadingSpinner } from '../components/ui';
import type { Column, BadgeVariant } from '../components/ui';

// ─── Types ────────────────────────────────────────────────────────────────────

interface VendorUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  status: 'active' | 'pending';
  acceptedAt?: string;
}

interface VendorSubmission {
  id: string;
  title: string;
  type: string;
  status: string;
  createdAt: string;
}

interface Vendor {
  id: string;
  name: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  lastAssessment?: string;
  pendingItems: number;
  expiringDocs: number;
  users?: VendorUser[];
  recentSubmissions?: VendorSubmission[];
}

interface DashboardData {
  totalVendors: number;
  highRiskVendors: number;
  pendingSubmissions: number;
  expiringDocuments: number;
  vendors: Vendor[];
}

interface ExpiringDocument {
  vendorName: string;
  documentTitle: string;
  type: string;
  expirationDate: string;
}

interface ExpiringData {
  within30: ExpiringDocument[];
  within60: ExpiringDocument[];
  within90: ExpiringDocument[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const RISK_BADGE_MAP: Record<string, BadgeVariant> = {
  LOW: 'success',
  MEDIUM: 'warning',
  HIGH: 'danger',
  CRITICAL: 'danger',
};

const SUBMISSION_STATUS_MAP: Record<string, BadgeVariant> = {
  APPROVED: 'success',
  PENDING: 'warning',
  REJECTED: 'danger',
  SUBMITTED: 'info',
  DRAFT: 'neutral',
};

const DOCUMENT_TYPES = [
  { value: 'SOC2_REPORT', label: 'SOC 2 Report' },
  { value: 'INSURANCE_CERT', label: 'Insurance Certificate' },
  { value: 'COMPLIANCE_ATTESTATION', label: 'Compliance Attestation' },
  { value: 'PRIVACY_POLICY', label: 'Privacy Policy' },
  { value: 'BUSINESS_CONTINUITY', label: 'Business Continuity Plan' },
];

type TabKey = 'overview' | 'expiring';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'expiring', label: 'Expiring Documents' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function VendorCompliancePage() {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();

  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [expandedVendorId, setExpandedVendorId] = useState<string | null>(null);

  // Invite user dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteVendorId, setInviteVendorId] = useState<string | null>(null);
  const [inviteForm, setInviteForm] = useState({ email: '', firstName: '', lastName: '' });

  // Request documents dialog
  const [showRequestDocsDialog, setShowRequestDocsDialog] = useState(false);
  const [requestDocsVendorId, setRequestDocsVendorId] = useState<string | null>(null);
  const [requestDocsForm, setRequestDocsForm] = useState({
    documentTypes: [] as string[],
    message: '',
  });

  // Add vendor dialog
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [addVendorForm, setAddVendorForm] = useState({
    name: '',
    category: '',
    contactEmail: '',
    website: '',
    currentRiskLevel: '' as string,
  });

  // ─── Queries ──────────────────────────────────────────────────────────────────

  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['vendor-compliance-dashboard', teamId],
    queryFn: () => vendorComplianceApi.dashboard(teamId!),
    enabled: !!teamId,
  });

  const dashboard: DashboardData | undefined = dashboardData?.data;

  const { data: expiringData, isLoading: isExpiringLoading } = useQuery({
    queryKey: ['vendor-compliance-expiring', teamId],
    queryFn: () => vendorComplianceApi.expiring(teamId!),
    enabled: !!teamId && activeTab === 'expiring',
  });

  const expiring: ExpiringData | undefined = expiringData?.data;

  // ─── Mutations ────────────────────────────────────────────────────────────────

  const inviteUserMutation = useMutation({
    mutationFn: (data: { email: string; firstName: string; lastName: string }) =>
      vendorComplianceApi.inviteUser(teamId!, inviteVendorId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-compliance-dashboard'] });
      // No invitation email is sent and the vendor portal is not built yet, so
      // say the invite was only recorded (2026-10-17 module review).
      toast(
        'Vendor user recorded. Email invitations are not available yet; contact the vendor directly.',
        { icon: 'ℹ️', duration: 8000 },
      );
      setShowInviteDialog(false);
      setInviteForm({ email: '', firstName: '', lastName: '' });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to invite user');
    },
  });

  const requestDocsMutation = useMutation({
    mutationFn: (data: { documentTypes: string[]; message: string }) =>
      vendorComplianceApi.requestDocuments(teamId!, requestDocsVendorId!, data),
    onSuccess: () => {
      // The backend records the request in the audit log but cannot email it
      // (delivered: false), so never claim it was sent (2026-10-17 module review).
      toast(
        'Document request logged, but not sent: email delivery to vendors is not available yet. Contact the vendor directly.',
        { icon: 'ℹ️', duration: 8000 },
      );
      setShowRequestDocsDialog(false);
      setRequestDocsForm({ documentTypes: [], message: '' });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to send document request');
    },
  });

  const calculateRiskMutation = useMutation({
    mutationFn: (vendorId: string) => vendorComplianceApi.calculateRisk(teamId!, vendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-compliance-dashboard'] });
      toast.success('Risk score calculated successfully');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to calculate risk score');
    },
  });

  const addVendorMutation = useMutation({
    mutationFn: (data: { name: string; category?: string; contactEmail?: string; website?: string; currentRiskLevel?: string }) =>
      vendorComplianceApi.createVendor(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-compliance-dashboard'] });
      toast.success('Vendor added successfully');
      setShowAddVendor(false);
      setAddVendorForm({ name: '', category: '', contactEmail: '', website: '', currentRiskLevel: '' });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to add vendor');
    },
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleOpenInvite = (vendorId: string) => {
    setInviteVendorId(vendorId);
    setInviteForm({ email: '', firstName: '', lastName: '' });
    setShowInviteDialog(true);
  };

  const handleSubmitInvite = () => {
    if (!inviteForm.email) return;
    inviteUserMutation.mutate(inviteForm);
  };

  const handleOpenRequestDocs = (vendorId: string) => {
    setRequestDocsVendorId(vendorId);
    setRequestDocsForm({ documentTypes: [], message: '' });
    setShowRequestDocsDialog(true);
  };

  const handleToggleDocType = (docType: string) => {
    setRequestDocsForm((prev) => ({
      ...prev,
      documentTypes: prev.documentTypes.includes(docType)
        ? prev.documentTypes.filter((t) => t !== docType)
        : [...prev.documentTypes, docType],
    }));
  };

  const handleSubmitRequestDocs = () => {
    if (requestDocsForm.documentTypes.length === 0) return;
    requestDocsMutation.mutate(requestDocsForm);
  };

  // ─── Table columns ───────────────────────────────────────────────────────────

  const vendorColumns: Column<Vendor>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      sortValue: (row) => row.name,
      render: (row) => <span className="font-medium text-navy-900">{row.name}</span>,
    },
    {
      key: 'riskLevel',
      header: 'Risk Level',
      sortable: true,
      sortValue: (row) => row.riskLevel,
      render: (row) => (
        <StatusBadge variant={RISK_BADGE_MAP[row.riskLevel] || 'neutral'}>
          {row.riskLevel}
        </StatusBadge>
      ),
    },
    {
      key: 'lastAssessment',
      header: 'Last Assessment',
      sortable: true,
      sortValue: (row) => row.lastAssessment || '',
      render: (row) => (
        <span className="text-gray-500">
          {row.lastAssessment ? new Date(row.lastAssessment).toLocaleDateString() : '--'}
        </span>
      ),
    },
    {
      key: 'pendingItems',
      header: 'Pending Items',
      sortable: true,
      sortValue: (row) => row.pendingItems,
      render: (row) => (
        <span className={clsx(row.pendingItems > 0 ? 'font-medium text-amber-600' : 'text-gray-500')}>
          {row.pendingItems}
        </span>
      ),
    },
    {
      key: 'expiringDocs',
      header: 'Expiring Docs',
      sortable: true,
      sortValue: (row) => row.expiringDocs,
      render: (row) => (
        <span className={clsx(row.expiringDocs > 0 ? 'font-medium text-red-600' : 'text-gray-500')}>
          {row.expiringDocs}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpandedVendorId(expandedVendorId === row.id ? null : row.id);
          }}
          className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-teal-600 hover:bg-teal-50 transition-colors"
        >
          Details
          <ChevronDownIcon
            className={clsx(
              'h-3.5 w-3.5 transition-transform duration-200',
              expandedVendorId === row.id && 'rotate-180',
            )}
          />
        </button>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────────

  if (!teamId) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <FeatureGate feature="vendor_compliance">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <PageHeader
            title="Vendor Compliance Portal"
            description="Monitor third-party vendor compliance, track document submissions, and manage risk assessments."
          />
          <button
            onClick={() => setShowAddVendor(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add Vendor
          </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={BuildingOfficeIcon}
            label="Total Vendors"
            value={dashboard?.totalVendors ?? 0}
          />
          <StatCard
            icon={ExclamationTriangleIcon}
            label="High Risk Vendors"
            value={dashboard?.highRiskVendors ?? 0}
          />
          <StatCard
            icon={ClockIcon}
            label="Pending Submissions"
            value={dashboard?.pendingSubmissions ?? 0}
          />
          <StatCard
            icon={DocumentTextIcon}
            label="Expiring Documents"
            value={dashboard?.expiringDocuments ?? 0}
          />
        </div>

        {/* Tab Bar */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors',
                  activeTab === tab.key
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab
            vendors={dashboard?.vendors || []}
            isLoading={isDashboardLoading}
            expandedVendorId={expandedVendorId}
            columns={vendorColumns}
            onInviteUser={handleOpenInvite}
            onRequestDocs={handleOpenRequestDocs}
            onCalculateRisk={(vendorId) => calculateRiskMutation.mutate(vendorId)}
            isCalculatingRisk={calculateRiskMutation.isPending}
          />
        )}

        {activeTab === 'expiring' && (
          <ExpiringDocumentsTab
            expiring={expiring}
            isLoading={isExpiringLoading}
          />
        )}

        {/* Invite Vendor User Dialog */}
        <Transition appear show={showInviteDialog} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowInviteDialog(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      Invite Vendor User
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      Record a vendor representative. Email invitations and the vendor portal are not available yet.
                    </p>

                    <div className="mt-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={inviteForm.email}
                          onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                          placeholder="vendor@example.com"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">First Name</label>
                          <input
                            type="text"
                            value={inviteForm.firstName}
                            onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Last Name</label>
                          <input
                            type="text"
                            value={inviteForm.lastName}
                            onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setShowInviteDialog(false)}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmitInvite}
                        disabled={!inviteForm.email || inviteUserMutation.isPending}
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
                      >
                        <EnvelopeIcon className="h-4 w-4" />
                        {inviteUserMutation.isPending ? 'Saving...' : 'Record Invitation'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Request Documents Dialog */}
        <Transition appear show={showRequestDocsDialog} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowRequestDocsDialog(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      Request Documents
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      Select the document types you need from this vendor. The request is logged for your records; email delivery is not available yet, so contact the vendor directly.
                    </p>

                    <div className="mt-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Document Types
                        </label>
                        <div className="space-y-2">
                          {DOCUMENT_TYPES.map((docType) => (
                            <label
                              key={docType.value}
                              className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={requestDocsForm.documentTypes.includes(docType.value)}
                                onChange={() => handleToggleDocType(docType.value)}
                                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                              />
                              <span className="text-sm text-gray-700">{docType.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Message</label>
                        <textarea
                          value={requestDocsForm.message}
                          onChange={(e) =>
                            setRequestDocsForm({ ...requestDocsForm, message: e.target.value })
                          }
                          rows={3}
                          placeholder="Optional message to include with the request..."
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setShowRequestDocsDialog(false)}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmitRequestDocs}
                        disabled={
                          requestDocsForm.documentTypes.length === 0 || requestDocsMutation.isPending
                        }
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
                      >
                        <DocumentTextIcon className="h-4 w-4" />
                        {requestDocsMutation.isPending ? 'Saving...' : 'Log Request'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Add Vendor Dialog */}
        <Transition appear show={showAddVendor} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowAddVendor(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      Add Vendor
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      Add a new third-party vendor to track their compliance.
                    </p>

                    <div className="mt-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Vendor Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={addVendorForm.name}
                          onChange={(e) => setAddVendorForm({ ...addVendorForm, name: e.target.value })}
                          placeholder="e.g. Acme Corp"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <input
                          type="text"
                          value={addVendorForm.category}
                          onChange={(e) => setAddVendorForm({ ...addVendorForm, category: e.target.value })}
                          placeholder="e.g. Core Banking, IT Services"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Contact Email</label>
                        <input
                          type="email"
                          value={addVendorForm.contactEmail}
                          onChange={(e) => setAddVendorForm({ ...addVendorForm, contactEmail: e.target.value })}
                          placeholder="contact@vendor.com"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Website</label>
                        <input
                          type="url"
                          value={addVendorForm.website}
                          onChange={(e) => setAddVendorForm({ ...addVendorForm, website: e.target.value })}
                          placeholder="https://vendor.com"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Initial Risk Level</label>
                        <select
                          value={addVendorForm.currentRiskLevel}
                          onChange={(e) => setAddVendorForm({ ...addVendorForm, currentRiskLevel: e.target.value })}
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        >
                          <option value="">Select risk level</option>
                          <option value="LOW">Low</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="HIGH">High</option>
                          <option value="CRITICAL">Critical</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setShowAddVendor(false)}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          if (!addVendorForm.name.trim()) {
                            toast.error('Vendor name is required');
                            return;
                          }
                          const payload: any = { name: addVendorForm.name.trim() };
                          if (addVendorForm.category) payload.category = addVendorForm.category;
                          if (addVendorForm.contactEmail) payload.contactEmail = addVendorForm.contactEmail;
                          if (addVendorForm.website) payload.website = addVendorForm.website;
                          if (addVendorForm.currentRiskLevel) payload.currentRiskLevel = addVendorForm.currentRiskLevel;
                          addVendorMutation.mutate(payload);
                        }}
                        disabled={!addVendorForm.name.trim() || addVendorMutation.isPending}
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
                      >
                        <PlusIcon className="h-4 w-4" />
                        {addVendorMutation.isPending ? 'Adding...' : 'Add Vendor'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({
  vendors,
  isLoading,
  expandedVendorId,
  columns,
  onInviteUser,
  onRequestDocs,
  onCalculateRisk,
  isCalculatingRisk,
}: {
  vendors: Vendor[];
  isLoading: boolean;
  expandedVendorId: string | null;
  columns: Column<Vendor>[];
  onInviteUser: (vendorId: string) => void;
  onRequestDocs: (vendorId: string) => void;
  onCalculateRisk: (vendorId: string) => void;
  isCalculatingRisk: boolean;
}) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm">
        <EmptyState
          icon={BuildingOfficeIcon}
          title="No vendors found"
          description="Add your first vendor to start tracking third-party compliance."
        />
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Vendors Table with Expandable Rows */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {vendors.map((vendor) => (
                <Fragment key={vendor.id}>
                  <tr className="transition-colors hover:bg-gray-50">
                    {columns.map((col) => (
                      <td key={col.key} className="py-3 px-4 text-sm text-gray-700">
                        {col.render(vendor)}
                      </td>
                    ))}
                  </tr>
                  {expandedVendorId === vendor.id && (
                    <tr>
                      <td colSpan={columns.length} className="bg-gray-50 px-4 py-0">
                        <VendorDetailPanel
                          vendor={vendor}
                          onInviteUser={() => onInviteUser(vendor.id)}
                          onRequestDocs={() => onRequestDocs(vendor.id)}
                          onCalculateRisk={() => onCalculateRisk(vendor.id)}
                          isCalculatingRisk={isCalculatingRisk}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Vendor Detail Panel ──────────────────────────────────────────────────────

function VendorDetailPanel({
  vendor,
  onInviteUser,
  onRequestDocs,
  onCalculateRisk,
  isCalculatingRisk,
}: {
  vendor: Vendor;
  onInviteUser: () => void;
  onRequestDocs: () => void;
  onCalculateRisk: () => void;
  isCalculatingRisk: boolean;
}) {
  const users = vendor.users || [];
  const submissions = (vendor.recentSubmissions || []).slice(0, 5);

  return (
    <div className="py-4 space-y-5">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onInviteUser}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Invite Vendor User
        </button>
        <button
          onClick={onRequestDocs}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <DocumentTextIcon className="h-3.5 w-3.5" />
          Request Documents
        </button>
        <button
          onClick={onCalculateRisk}
          disabled={isCalculatingRisk}
          className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50 transition-colors"
        >
          <CalculatorIcon className="h-3.5 w-3.5" />
          {isCalculatingRisk ? 'Calculating...' : 'Calculate Risk Score'}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Vendor Users Section */}
        <Disclosure defaultOpen>
          {({ open }) => (
            <div className="rounded-lg border border-gray-200 bg-white">
              <Disclosure.Button className="flex w-full items-center justify-between px-4 py-3 text-left">
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-navy-900">
                    Vendor Users ({users.length})
                  </span>
                </div>
                <ChevronDownIcon
                  className={clsx(
                    'h-4 w-4 text-gray-400 transition-transform duration-200',
                    open && 'rotate-180',
                  )}
                />
              </Disclosure.Button>
              <Disclosure.Panel className="border-t border-gray-200 px-4 py-3">
                {users.length === 0 ? (
                  <p className="text-sm text-gray-400 py-2">No users invited yet.</p>
                ) : (
                  <div className="space-y-2">
                    {users.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                          {(user.firstName || user.lastName) && (
                            <p className="text-xs text-gray-500">
                              {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <StatusBadge variant={user.status === 'active' ? 'success' : 'warning'}>
                            {user.status}
                          </StatusBadge>
                          {user.acceptedAt && (
                            <span className="text-xs text-gray-400">
                              {new Date(user.acceptedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Disclosure.Panel>
            </div>
          )}
        </Disclosure>

        {/* Recent Submissions Section */}
        <Disclosure defaultOpen>
          {({ open }) => (
            <div className="rounded-lg border border-gray-200 bg-white">
              <Disclosure.Button className="flex w-full items-center justify-between px-4 py-3 text-left">
                <div className="flex items-center gap-2">
                  <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-navy-900">
                    Recent Submissions ({submissions.length})
                  </span>
                </div>
                <ChevronDownIcon
                  className={clsx(
                    'h-4 w-4 text-gray-400 transition-transform duration-200',
                    open && 'rotate-180',
                  )}
                />
              </Disclosure.Button>
              <Disclosure.Panel className="border-t border-gray-200 px-4 py-3">
                {submissions.length === 0 ? (
                  <p className="text-sm text-gray-400 py-2">No submissions yet.</p>
                ) : (
                  <div className="space-y-2">
                    {submissions.map((sub) => (
                      <div
                        key={sub.id}
                        className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{sub.title}</p>
                          <p className="text-xs text-gray-500">{sub.type}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <StatusBadge
                            variant={SUBMISSION_STATUS_MAP[sub.status] || 'neutral'}
                          >
                            {sub.status}
                          </StatusBadge>
                          <span className="text-xs text-gray-400">
                            {new Date(sub.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Disclosure.Panel>
            </div>
          )}
        </Disclosure>
      </div>
    </div>
  );
}

// ─── Expiring Documents Tab ───────────────────────────────────────────────────

function ExpiringDocumentsTab({
  expiring,
  isLoading,
}: {
  expiring: ExpiringData | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!expiring) {
    return (
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm">
        <EmptyState
          icon={DocumentTextIcon}
          title="No expiring documents"
          description="All vendor documents are current."
        />
      </div>
    );
  }

  const hasAny =
    (expiring.within30?.length || 0) +
      (expiring.within60?.length || 0) +
      (expiring.within90?.length || 0) >
    0;

  if (!hasAny) {
    return (
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm">
        <EmptyState
          icon={ShieldCheckIcon}
          title="No expiring documents"
          description="All vendor documents are current. No action needed."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Within 30 Days */}
      {expiring.within30 && expiring.within30.length > 0 && (
        <ExpiringSection
          title="Within 30 Days"
          documents={expiring.within30}
          borderColor="border-red-300"
          bgColor="bg-red-50"
          textColor="text-red-700"
          iconColor="text-red-500"
        />
      )}

      {/* Within 60 Days */}
      {expiring.within60 && expiring.within60.length > 0 && (
        <ExpiringSection
          title="Within 60 Days"
          documents={expiring.within60}
          borderColor="border-amber-300"
          bgColor="bg-amber-50"
          textColor="text-amber-700"
          iconColor="text-amber-500"
        />
      )}

      {/* Within 90 Days */}
      {expiring.within90 && expiring.within90.length > 0 && (
        <ExpiringSection
          title="Within 90 Days"
          documents={expiring.within90}
          borderColor="border-yellow-300"
          bgColor="bg-yellow-50"
          textColor="text-yellow-700"
          iconColor="text-yellow-500"
        />
      )}
    </div>
  );
}

function ExpiringSection({
  title,
  documents,
  borderColor,
  bgColor,
  textColor,
  iconColor,
}: {
  title: string;
  documents: ExpiringDocument[];
  borderColor: string;
  bgColor: string;
  textColor: string;
  iconColor: string;
}) {
  return (
    <div className={clsx('rounded-lg border bg-white shadow-sm overflow-hidden', borderColor)}>
      <div className={clsx('flex items-center gap-2 px-4 py-3 border-b', bgColor, borderColor)}>
        <ExclamationTriangleIcon className={clsx('h-4 w-4', iconColor)} />
        <h3 className={clsx('text-sm font-semibold', textColor)}>
          {title} ({documents.length})
        </h3>
      </div>
      <div className="divide-y divide-gray-100">
        {documents.map((doc, index) => (
          <div key={`${doc.vendorName}-${doc.documentTitle}-${index}`} className="flex items-center justify-between px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-navy-900">{doc.vendorName}</p>
              <p className="text-sm text-gray-600">{doc.documentTitle}</p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <StatusBadge variant="neutral">{doc.type}</StatusBadge>
              <span className="text-sm text-gray-500 tabular-nums">
                {new Date(doc.expirationDate).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
